import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import { cn } from '../../lib/utils';
import type { RoundClimb } from '../../types/competition-round';

interface RoundClimbThumbnailProps {
  roundClimb: RoundClimb;
  /** Sizing of the thumbnail itself; the dialog is the same whatever it is. */
  className?: string;
  /**
   * Takes the tap instead of the lightbox. For the one caller that has something
   * better to do with the picture than enlarge it: an admin on a lead round, for
   * whom tapping the photo is how the route's holds get circled (see
   * `RouteHoldsDialog`). Everybody else still just looks at it.
   */
  onOpen?: () => void;
}

/**
 * The problem's picture, small, and — tapped — big. The round page is read
 * from the mat, where "which one is Blocco 4" is answered by the photo, not
 * the number; but a 64px square cannot answer it, so the tap opens the same
 * picture at the size of the screen.
 *
 * Renders nothing when there is no picture: what stands in for it (a
 * placeholder, or the admin's upload buttons) is the caller's decision.
 *
 * Every tap in here stops where it is. The rows this sits on open something
 * of their own — the judge's scoring page, the athlete's attempt buttons —
 * and looking at the photo must not also do that. That goes for the dialog
 * too: it is portalled out of the DOM but not out of the React tree, so a tap
 * on its overlay would otherwise reach the row underneath.
 */
export function RoundClimbThumbnail({
  roundClimb,
  className,
  onOpen,
}: RoundClimbThumbnailProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  if (!roundClimb.image_small_url) return null;

  const name = roundClimb.display_name ?? '';

  return (
    <span className="contents" onClick={(event) => event.stopPropagation()}>
      <button
        type="button"
        className={cn(
          'shrink-0 overflow-hidden rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          className
        )}
        // Where the tap does something other than enlarge the photo, the label
        // says what — a thumbnail that says "view photo" and circles holds is a
        // button nobody reading it aloud could use.
        aria-label={onOpen ? t('leadRounds.draw.openHint') : t('competitionRounds.viewPhoto', { name })}
        title={onOpen ? t('leadRounds.draw.openHint') : undefined}
        data-testid="round-climb-thumbnail"
        onClick={() => (onOpen ? onOpen() : setOpen(true))}
      >
        <img src={roundClimb.image_small_url} alt={name} className="h-full w-full object-cover" />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl p-2 sm:p-4" aria-describedby={undefined}>
          <DialogTitle className="sr-only">{name}</DialogTitle>
          <img
            src={roundClimb.image_url ?? roundClimb.image_small_url}
            alt={name}
            className="max-h-[80vh] w-full rounded-md object-contain"
            data-testid="round-climb-photo"
          />
        </DialogContent>
      </Dialog>
    </span>
  );
}
